/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
/**
 * Utils
 */
import { session } from 'auth/session'
import { useCurrentOrg } from 'common/hooks/useCurrentOrg'
/**
 * Pages
 */
import Authentication from 'auth/components/Authentication'
import CliAuth from 'auth/components/CliAuth'
import { nonBrowserCallback } from 'auth/utils'
import { isEmpty } from 'lodash'
import PageVisibility from 'react-page-visibility'
import { useInterval, useMount } from 'react-use'

import LoadingPage from 'common/components/LoadingPage'
import { useActivePage } from 'common/hooks/useActivePage'
import { useOnlineStatus } from 'common/hooks/useOnlineStatus'
import { useOrgNameInUrl } from 'common/hooks/useOrgNameInUrl'
import { routes } from 'routes'
import { usePathReporter } from '../hooks/usePathReporter'
import NoInternetConnectionPage from './NoInternetConnectionPage'
import OrgNotFoundPage from './OrgNotFoundPage'

/**
 * If you're within 5 minutes of the token expiring, consider it invalid.
 * The idea here is to catch a soon-expiring session, so users don't get
 * failed requests as they navigate around before one of these ticks happens.
 */
const timeBuffer = 300000

/**
 * Ensures that the user is logged in to see this route. If not,
 * the user is shown the auth flow
 */
export const PrivateRoute = () => {
  const [searchParams] = useSearchParams()
  const { org, isLoading } = useCurrentOrg()
  const orgNotFound = !isLoading && isEmpty(org)
  const location = useLocation()
  const activePage = useActivePage()
  const navigate = useNavigate()
  /**
   * Add page view tracking events
   */
  usePathReporter()
  /**
   * Grab the orgName from the URL, if there is one there
   */
  const orgNameInUrl = useOrgNameInUrl()

  /**
   * Use online status hook here to notify with a snackbar if there's no internet connection
   */
  const { isOnline } = useOnlineStatus()

  const { pathname } = location
  const isCli = searchParams?.get('client') === 'cli'
  const transactionId = searchParams?.get('transactionId')

  useMount(async () => {
    /**
     * You're logged in + setup, so let's communicate to the CLI your session
     */
    if (session.isAccountSetup() && isCli && transactionId) {
      await nonBrowserCallback(transactionId, {
        access_token: session.get('authAccessToken'),
        id_token: session.get('authIdToken'),
        refresh_token: session.get('authRefreshToken'),
        expires_at: session.get('authExpires'),
        username: session.get('user').userName,
        user_uid: session.get('user').userUid,
      })
    }
  })

  /**
   * Handle redirects
   */

  useEffect(() => {
    /**
     * If landed on dashboard.serverless (dev or prod) then redirect to app.serverless
     */
    if (window.location.host.includes('dashboard.serverless')) {
      window.location.href = window.location.href.replace('dashboard.serverless', 'app.serverless')
    }
    /**
     * If current path is an org path and orgName is not in the URL, add it and redirect to the new URL
     */
    const isValidRoute =
      routes?.[0]?.children?.every((route) => `/${route.path}` !== pathname) || pathname === '/'
    if (!!org?.orgName && !orgNameInUrl && isValidRoute) {
      navigate({
        pathname: `${org?.orgName}${pathname}`,
        search: location.search,
      })
    }
  }, [location, org])

  const checkSession = () => {
    if (session.isLoggedIn()) {
      const grantedTimeMs = session.get('authTimeGranted')
      /**
       * The expiration time is supposed to come from the token response
       * of POST /tokens, which is saved here: session.get('authExpires')
       * but in my testing, the expiration is always 10 hours
       */
      const expiresInMs = 864000 * 1000 // 10 hours

      if (Date.now() > expiresInMs + grantedTimeMs + timeBuffer) {
        session.destroy()
      }
    }
  }

  /**
   * Check on mount and periodically to see if the session is valid
   */
  useMount(checkSession)
  useInterval(checkSession, session.isLoggedIn() ? 30000 : null)

  /**
   * If there's no internet connection and user try to navigate return this view
   */
  if (!isOnline) {
    return <NoInternetConnectionPage />
  }
  if (activePage?.handle?.public) {
    return <Outlet />
  }
  if (!session.isLoggedIn() || !session.isAccountSetup()) {
    /**
     * If user is not logged or account is not setup, show the auth flow
     */
    return <Authentication />
  }
  /**
   * Show CLI logged in experience if the user is logged in and account is setup
   */
  if (session.isAccountSetup() && isCli) {
    return <CliAuth />
  }

  /**
   * You're logged in and setup, just use the component specified for the route
   */
  if (session.isLoggedIn() && session.isAccountSetup()) {
    return (
      /**
       * Page visibility is used to check for session when user is active again in tab
       */
      <PageVisibility onChange={(isVisible) => isVisible && session.isLoggedIn() && checkSession()}>
        {isLoading ? <LoadingPage /> : orgNotFound ? <OrgNotFoundPage /> : <Outlet />}
      </PageVisibility>
    )
  }
}
